import { lazy } from "react";

import type { ComponentType, LazyExoticComponent } from "react";

export type Preloadable<Props extends object> = LazyExoticComponent<ComponentType<Props>> & {
  readonly preload: () => Promise<void>;
};

// WHY: `React.lazy` loads only on render, so a dialog opens blank; preloading on intent (hover, focus, press) loads its code before the click lands. A failed load is forgotten so the next intent tries again.
export const lazyWithPreload = <Props extends object>(
  load: () => Promise<ComponentType<Props>>,
): Preloadable<Props> => {
  let loading: Promise<{ default: ComponentType<Props> }> | undefined;
  const start = async (): Promise<{ default: ComponentType<Props> }> => {
    try {
      return { default: await load() };
    } catch (error) {
      loading = undefined;
      throw error;
    }
  };
  const loadOnce = async (): Promise<{ default: ComponentType<Props> }> => {
    loading ??= start();
    return loading;
  };
  return Object.assign(lazy(loadOnce), {
    preload: async () => {
      await loadOnce();
    },
  });
};

// WHY: passed as `onIntent` (hover, focus) to the control that opens a lazy dialog or panel; a failed preload stays silent and the open retries.
export const preloadOnIntent =
  (...targets: readonly { readonly preload: () => Promise<void> }[]): (() => void) =>
  () => {
    for (const target of targets) {
      void target.preload().catch(() => null);
    }
  };
